/**
 * Coaching card and welcome modal UI.
 *
 * Pure presentational primitives — no provider awareness. The overlay
 * passes copy + callbacks; this component just renders them.
 *
 * Phase 7 hardening:
 *   - `aria-describedby` linkage between title/body and the dialog root
 *   - Focus trap inside the modal variant so Tab/Shift-Tab cycle inside
 *   - Focus restoration on unmount (modal variant)
 *   - Reduced-motion aware entrance (no fade-in when the OS asks)
 *   - `aria-modal="true"` on the modal-center variant only — side cards
 *     are non-blocking and stay `aria-modal="false"`
 */

import { useEffect, useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { useReducedMotion } from "./useReducedMotion";

interface CardActions {
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  onDismiss: () => void;
}

interface CoachingCardProps extends CardActions {
  title: string;
  body: ReactNode;
  hint?: ReactNode;
  stepLabel?: string;
  /** Inline style for absolute positioning. */
  style?: React.CSSProperties;
  className?: string;
  /**
   * When true, traps Tab/Shift+Tab focus inside the card and marks the
   * dialog as `aria-modal="true"`. The modal-center variant uses this;
   * spotlight side-cards do not.
   */
  modal?: boolean;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableInside(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function CoachingCard({
  title,
  body,
  hint,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  onDismiss,
  stepLabel,
  style,
  className,
  modal = false,
}: CoachingCardProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const bodyId = useId();
  const reduced = useReducedMotion();

  // Move focus to the primary button on mount. Don't steal focus if the
  // user is mid-type in a non-modal side card. Modal variant captures
  // the previously focused element for restoration on unmount.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const active = document.activeElement as HTMLElement | null;
    if (modal && active && active !== document.body) {
      restoreFocusRef.current = active;
    }
    if (!modal && active && active.tagName !== "BODY") {
      const editable =
        active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.isContentEditable;
      if (editable) return;
    }
    const primary = root.querySelector<HTMLButtonElement>(
      "[data-onboarding-card-primary]",
    );
    primary?.focus({ preventScroll: true });
    return () => {
      const target = restoreFocusRef.current;
      if (modal && target && document.contains(target)) {
        try {
          target.focus({ preventScroll: true });
        } catch {
          // ignore
        }
      }
    };
  }, [modal]);

  // Escape = dismiss. Modal variant also traps Tab/Shift-Tab cycling.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onDismiss();
        return;
      }
      if (modal && e.key === "Tab") {
        const focusables = focusableInside(root);
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const current = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (current === first || !root.contains(current)) {
            e.preventDefault();
            last.focus();
          }
        } else if (current === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
  }, [onDismiss, modal]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal={modal ? "true" : "false"}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      data-onboarding-card
      className={
        className ??
        "pointer-events-auto w-[320px] max-w-[calc(100vw-32px)] rounded-xl border border-slate-200 bg-white p-4 shadow-2xl ring-1 ring-black/5"
      }
      style={{
        ...style,
        animation: reduced
          ? undefined
          : "staylayer-mentor-fade-in 180ms ease-out",
      }}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          {stepLabel ? (
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-500">
              {stepLabel}
            </div>
          ) : null}
          <div id={titleId} className="text-sm font-semibold text-slate-900">
            {title}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close walkthrough"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div id={bodyId} className="text-sm leading-relaxed text-slate-600">
        {body}
      </div>

      {hint ? (
        <div className="mt-2 text-xs leading-relaxed text-slate-400">
          {hint}
        </div>
      ) : null}

      <div className="mt-4 flex items-center justify-end gap-2">
        {secondaryLabel && onSecondary ? (
          <button
            type="button"
            onClick={onSecondary}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
          >
            {secondaryLabel}
          </button>
        ) : null}
        <button
          type="button"
          data-onboarding-card-primary
          onClick={onPrimary}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}

interface WelcomeModalProps extends CardActions {
  title: string;
  body: ReactNode;
  stepLabel?: string;
}

/**
 * Centered modal variant used by e0_welcome and e10_post_publish.
 * Same card body, rendered in a centered scrim with no spotlight
 * cutout and with focus-trap enabled.
 */
export function WelcomeModal(props: WelcomeModalProps) {
  const { stepLabel = "Build With Me · Walkthrough" } = props;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 9999,
      }}
    >
      <CoachingCard
        {...props}
        modal
        className="pointer-events-auto w-105 max-w-[calc(100vw-48px)] rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl ring-1 ring-black/5"
        stepLabel={stepLabel}
      />
    </div>
  );
}
